import React, { useState, useEffect, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import { toPng } from "html-to-image";
import { QrCode, Gift, Ticket, MapPin, Printer, Expand, Info, BookOpen, Filter } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function BusinessQRs({ business, activeQrFilter, onClearQrFilter }) {
  const bid = business?.id;
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const directoryUrl = `${baseUrl}/directorio/${business?.postal_code || ""}`;
  const pointsUrl = `${baseUrl}/escanear/${bid}?action=points`;
  const raffleUrl = `${baseUrl}/escanear/${bid}?action=raffle`;

  const [menuSlug, setMenuSlug] = useState(null);
  const [directoryName, setDirectoryName] = useState(null);

  useEffect(() => {
    api.get("/business/menu")
      .then((r) => {
        if (r.data?.slug) setMenuSlug(r.data.slug);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!business?.postal_code) return;
    api.get(`/directory/search?postal_code=${business.postal_code}`)
      .then((r) => setDirectoryName(r.data.directory_name))
      .catch(() => {});
  }, [business?.postal_code]);

  const menuIdentifier = menuSlug || bid;
  const menuUrl = `${baseUrl}/menu/${menuIdentifier || ""}`;
  const menuUrlLabel = `gpsofertas.com/menu/${menuIdentifier || ""}`;

  const [modalIdx, setModalIdx] = useState(null);
  const posters = [
    {
      key: "cartel",
      icon: <MapPin className="w-5 h-5 text-[#7C3AED]" />,
      iconBg: "bg-[#F5F3FF]",
      title: "Cartel del directorio de tu zona",
      description: <>Imprime este cartel y pégalo en tu negocio. Tus clientes lo escanean y ven tu anuncio en el directorio <strong>{directoryName ? `"${directoryName}"` : `(CP ${business?.postal_code || ""})`}</strong>.</>,
      tip: <>Pégalo en la entrada, caja o vitrina. Cualquier cliente lo puede escanear sin registro.</>,
      url: directoryUrl,
      urlLabel: `gpsofertas.com/directorio/${business?.postal_code || ""}`,
      posterConfig: {
        overline: "DIRECTORIO LOCAL · OFERTAS",
        vecino: "¡VECINO!",
        headline: (<>Las mejores <span className="poster-grad">ofertas</span><br />de tu colonia<br />están AQUÍ</>),
        sub: "Descuentos · 2x1 · Recompensas · Promos diarias de los negocios de TU rumbo",
        scan: "ESCANEA Y DESCUBRE",
        qrUrl: directoryUrl,
        qrUrlLabel: `gpsofertas.com/directorio/${business?.postal_code || ""}`,
        footerLeftLabel: "Directorio",
        footerLeftName: directoryName || `OFERTAS CP ${business?.postal_code || ""}`,
        footerLeftHint: <>CP {business?.postal_code || ""}</>,
        footerRightMsg: <>Encuentra {business?.business_name || "este negocio"} y muchos más comercios cerca de ti.</>,
      },
    },
    {
      key: "menu",
      icon: <BookOpen className="w-5 h-5 text-[#0284C7]" />,
      iconBg: "bg-[#F0F9FF]",
      title: "Ver mi Menú / Catálogo digital",
      description: <>Cliente escanea → abre tu <strong>menú o catálogo digital</strong> directamente en su celular.</>,
      tip: <>Pégalo en las mesas, en la carta o en el mostrador para que tus clientes consulten tus productos y precios.</>,
      url: menuUrl,
      urlLabel: menuUrlLabel,
      posterConfig: {
        overline: "MENÚ DIGITAL · OFERTAS",
        vecino: "¡CONSULTA!",
        headline: (<>Escanea y<br /><span className="poster-grad">consulta</span><br />nuestro menú</>),
        sub: `Productos, precios y promociones · ${business?.business_name || "Tu comercio"}`,
        scan: "ESCANEA Y VE EL MENÚ",
        qrUrl: menuUrl,
        qrUrlLabel: menuUrlLabel,
        footerLeftLabel: "Comercio",
        footerLeftName: business?.business_name || "Tu comercio",
        footerLeftHint: <>Menú digital · Productos · Precios</>,
        footerRightMsg: <>¡Consulta nuestro catálogo desde tu celular!</>,
      },
    },
    {
      key: "puntos",
      icon: <Gift className="w-5 h-5 text-[#16A34A]" />,
      iconBg: "bg-[#F0FDF4]",
      title: "Acumular puntos por compra",
      description: <>Cliente escanea → automáticamente recibe <strong>puntos de lealtad</strong> y un boleto de rifa. No necesitas hacer nada.</>,
      tip: <>Requiere programa de lealtad activo. Pégalo en caja o donde el cliente paga. Idempotente: 1 vez por cliente por día.</>,
      url: pointsUrl,
      urlLabel: `gpsofertas.com/escanear/${bid}?action=points`,
      posterConfig: {
        overline: "PROGRAMA DE LEALTAD",
        vecino: "¡PUNTOS!",
        headline: (<>Escanea y<br /><span className="poster-grad">acumula</span><br />puntos</>),
        sub: "Por cada compra acumulas puntos · Canjea premios · Participas en la rifa mensual",
        scan: "ESCANEA Y RECIBE TUS PUNTOS",
        qrUrl: pointsUrl,
        qrUrlLabel: `gpsofertas.com/escanear/${bid}?action=points`,
        footerLeftLabel: "Comercio",
        footerLeftName: business?.business_name || "Tu comercio",
        footerLeftHint: <>Regístrate como cliente y escanea</>,
        footerRightMsg: <>¡Puntos automáticos en cada visita!</>,
      },
    },
    {
      key: "rifa",
      icon: <Ticket className="w-5 h-5 text-[#E05A47]" />,
      iconBg: "bg-[#FEF3F2]",
      title: "Otorgar boleto de rifa",
      description: <>Cliente escanea → recibe <strong>1 boleto para la rifa mensual</strong> de electrodomésticos. Sin acción tuya.</>,
      tip: <>No requiere programa de lealtad — cualquier negocio activo. Pégalo en la entrada o en la bag. Idempotente: 1 vez por cliente por día.</>,
      url: raffleUrl,
      urlLabel: `gpsofertas.com/escanear/${bid}?action=raffle`,
      posterConfig: {
        overline: "RIFA MENSUAL · OFERTAS",
        vecino: "¡BOLETO!",
        headline: (<>Escanea y<br /><span className="poster-grad">participa</span><br />en la rifa</>),
        sub: "Gana electrodomésticos · Boletos digitales · Sin costo para ti",
        scan: "ESCANEA Y RECIBE TU BOLETO",
        qrUrl: raffleUrl,
        qrUrlLabel: `gpsofertas.com/escanear/${bid}?action=raffle`,
        footerLeftLabel: "Comercio",
        footerLeftName: business?.business_name || "Tu comercio",
        footerLeftHint: <>Regístrate como cliente y escanea</>,
        footerRightMsg: <>¡Boleto gratis con cada visita!</>,
      },
    },
  ];

  const posterRef = useRef(null);
  const [printing, setPrinting] = useState(false);
  const [posterImgSrc, setPosterImgSrc] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);

  useEffect(() => {
    if (modalIdx === null) {
      setPosterImgSrc(null);
      return;
    }
    setPosterImgSrc(null);
    setImgLoading(true);
    const t = setTimeout(async () => {
      const node = posterRef.current;
      if (!node) return;
      try {
        const dataUrl = await toPng(node, { pixelRatio: 3, backgroundColor: "#ffffff" });
        setPosterImgSrc(dataUrl);
      } catch (err) {
        toast.error("No se pudo generar la vista previa del cartel");
      } finally {
        setImgLoading(false);
      }
    }, 50);
    return () => clearTimeout(t);
  }, [modalIdx]);

  const handlePrint = async () => {
    if (!posterImgSrc) return;
    setPrinting(true);
    try {
      const dataUrl = posterImgSrc;

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const cleanup = () => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      };

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(
        `<html><head><title>Imprimir QR</title><style>
          @page { size: letter; margin: 0; }
          html, body { margin: 0; padding: 0; height: 11in; }
          body { display: flex; align-items: center; justify-content: center; }
          img { display: block; width: auto; height: auto; max-width: 8.5in; max-height: 11in; }
        </style></head><body>
          <img src="${dataUrl}" />
        </body></html>`
      );
      doc.close();

      const img = doc.querySelector("img");
      img.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      };
      iframe.contentWindow.onafterprint = cleanup;
      setTimeout(cleanup, 60000);
    } catch (err) {
      toast.error("No se pudo generar el cartel para imprimir");
    } finally {
      setPrinting(false);
    }
  };

  const filteredPoster = activeQrFilter ? posters.find((p) => p.key === activeQrFilter) : null;
  const displayedPosters = filteredPoster ? [filteredPoster] : posters;

  useEffect(() => {
    if (filteredPoster) {
      setModalIdx(posters.findIndex((p) => p.key === filteredPoster.key));
    }
  }, [activeQrFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-2xl p-5 print:hidden">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-white grid place-items-center shrink-0">
            <QrCode className="w-5 h-5 text-[#7C3AED]" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-2xl tracking-tight">Tus códigos QR imprimibles</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Tus clientes escanean estos códigos impresos para interactuar con tu comercio, sin que tú hagas nada. Imprime cada uno y pégalo donde corresponda.
            </p>
          </div>
        </div>
      </div>

      {filteredPoster && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-[#DDD6FE] rounded-2xl p-4 shadow-sm print:hidden">
          <div className="flex items-center gap-2 text-sm text-[#5B21B6]">
            <Filter className="w-4 h-4 text-[#7C3AED] shrink-0" />
            <span>Filtrado por: <strong>{filteredPoster.title}</strong></span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClearQrFilter}
            className="rounded-full text-xs border-[#C4B5FD] text-[#5B21B6] hover:bg-[#EDE9FE]"
            data-testid="biz-qr-show-all-btn"
          >
            Mostrar todos los QR ({posters.length})
          </Button>
        </div>
      )}

      {displayedPosters.map((p) => {
        const realIdx = posters.findIndex((item) => item.key === p.key);
        return (
          <PosterCard
            key={p.key}
            {...p}
            onPreviewClick={() => setModalIdx(realIdx)}
            onPrint={() => setModalIdx(realIdx)}
          />
        );
      })}

      <Dialog open={modalIdx !== null} onOpenChange={(o) => !o && setModalIdx(null)}>
        <DialogContent className="max-w-4xl w-full p-3 sm:p-4" data-testid="biz-qr-modal">
          <DialogTitle className="sr-only">Cartel QR</DialogTitle>
          <div className="w-full max-h-[85vh] overflow-auto flex justify-center items-center min-h-[200px]">
            {imgLoading && <div className="text-sm text-muted-foreground py-10">Generando vista previa…</div>}
            {posterImgSrc && (
              <img
                src={posterImgSrc}
                alt="Cartel QR"
                className="max-w-full h-auto rounded-xl border border-border shadow-sm"
                data-testid="biz-qr-poster-preview-img"
              />
            )}
          </div>
          <div className="mt-4 flex justify-center">
            <Button onClick={handlePrint} disabled={printing || !posterImgSrc} className="btn-primary rounded-full" data-testid="biz-qr-modal-print">
              <Printer className="w-4 h-4 mr-2" /> {printing ? "Imprimiendo…" : "Imprimir / Guardar PDF"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Off-screen native-size poster used only to capture the print image; never shown to the user */}
      {modalIdx !== null && (
        <div style={{ position: "fixed", top: 0, left: -99999, width: POSTER_W_PX }} aria-hidden="true">
          <div ref={posterRef} style={{ width: POSTER_W_PX }}>
            <QRPosterCard
              {...posters[modalIdx]}
              posterConfig={{ ...posters[modalIdx].posterConfig, print: true }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const POSTER_W_PX = 816; // 8.5in @ 96dpi

function PosterCard({ icon, iconBg, title, description, tip, url, urlLabel, posterConfig, onPreviewClick }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-4 sm:p-6">
      <div className="grid md:grid-cols-[1fr_auto] gap-5 sm:gap-6 items-start">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${iconBg} grid place-items-center shrink-0`}>
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg sm:text-xl md:text-2xl tracking-tight leading-snug">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{description}</p>
            <div className="mt-3 flex items-start gap-2 bg-[#F5F3FF] border border-[#DDD6FE] rounded-lg p-3 text-xs">
              <Info className="w-4 h-4 mt-0.5 text-[#7C3AED] shrink-0" />
              <span className="text-zinc-700"><strong>Tip:</strong> {tip}</span>
            </div>
            <div className="mt-3 text-[11px] text-muted-foreground font-mono break-all">{urlLabel}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onPreviewClick}
          className="group relative rounded-xl border border-border overflow-hidden shadow-sm hover:shadow-md transition-shadow shrink-0 mx-auto md:mx-0 justify-self-center md:justify-self-end print:hidden"
          style={{ width: 180, height: 233 }}
          title="Ver cartel completo"
          data-testid="biz-qr-preview-btn"
        >
          <div className="origin-top-left absolute top-0 left-0" style={{ transform: "scale(0.22)", width: "8.5in", height: "11in" }}>
            <QRPoster {...posterConfig} />
          </div>
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg">
              <Expand className="w-5 h-5 text-[#7C3AED]" />
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

function QRPosterCard({ posterConfig }) {
  return <QRPoster {...posterConfig} />;
}

export function QRPoster({
  overline, vecino, headline, sub, scan, qrUrl, qrUrlLabel,
  footerLeftLabel, footerLeftName, footerLeftHint, footerRightMsg,
}) {
  return (
    <div className="poster mx-auto bg-white rounded-2xl shadow-lg print:shadow-none print:rounded-none" data-testid="biz-qr-poster">
      <div className="poster-top">
        <div className="poster-overline">{overline}</div>
        <div className="poster-vecino">{vecino}</div>
      </div>
      <div className="poster-main">
        <div className="poster-headline">{headline}</div>
        <div className="poster-sub">{sub}</div>
      </div>
      <div className="poster-qr-block">
        <div className="poster-scan">{scan}</div>
        <div className="poster-qr-frame">
          <QRCodeSVG value={qrUrl} size={340} level="H" includeMargin={false} />
        </div>
        <div className="poster-url">{qrUrlLabel}</div>
      </div>
      <div className="poster-footer">
        <div className="poster-footer-left">
          <div className="poster-dir-label">{footerLeftLabel}</div>
          <div className="poster-dir-name" style={{ fontSize: "1.6rem" }}>{footerLeftName}</div>
          <div className="poster-dir-cp" style={{ marginTop: "0.3rem" }}>{footerLeftHint}</div>
        </div>
        <div className="poster-footer-right">
          <span className="poster-sparkle !w-6 !h-6 !text-[#7C3AED]">✦</span>
          <div className="poster-footer-msg">{footerRightMsg}</div>
        </div>
      </div>
    </div>
  );
}